import type { ReactNode } from 'react'
import { TableHead } from '@/components/ui/table'
import { MIN_COLUMN_WIDTH, MAX_COLUMN_WIDTH } from '@/lib/tableColumnPrefs'
import { stickyCellClass } from '@/lib/stickyColumn'
import type { UseTableColumnsResult } from '@/hooks/useTableColumns'
import { ColumnsMenu } from './ColumnsMenu'
import { useColumnResize } from './useColumnResize'

/**
 * Where the Columns control lives: the FIRST visible header cell, decided here
 * so no table has to remember to place it and none can place it differently.
 *
 * The left edge is the one place that does not move. A table's rightmost column
 * is wherever the horizontal scroll happens to be and changes as columns are
 * hidden, so a control anchored there is somewhere different on every screen —
 * which is the whole reason this used to sit in a row of its own.
 *
 * It is absolutely positioned rather than laid out inline, so it cannot disturb
 * the alignment a table has chosen for that header; the cell gains left padding
 * to keep its own text off the button, and a narrow first column (an expand
 * chevron, a select-all checkbox) widens to fit rather than overlapping it.
 */
function rendersColumnsMenu(columnId: string, columns: UseTableColumnsResult): boolean {
  const visible = columns.visible
  return visible.length > 0 && visible[0].def.id === columnId
}

/**
 * The drag grip. Shared by both header flavours so they feel identical.
 *
 * ## Why it is visible rather than hover-only
 *
 * The first version was a 4px fully transparent strip that only showed a border
 * on hover. Nothing on the screen said a column could be resized, and finding
 * the strip meant hovering a 4px target you had no reason to aim at — so the
 * feature was, in practice, absent. It now renders a permanent short divider at
 * every resizable boundary, the way a spreadsheet does: quiet enough to read as
 * chrome, present enough to be aimed at. It grows to full height and takes the
 * primary colour on hover, focus and drag.
 *
 * The hit area is 10px CENTRED on the boundary (`translate-x-1/2`), not 4px
 * inside it, because a pointer approaching a column edge is as likely to be a
 * few pixels past it as before it. `z-20` keeps it above a sticky header's own
 * stacking context.
 */
function ColumnResizeHandle({
  width,
  dragging,
  handleProps,
}: Pick<ReturnType<typeof useColumnResize>, 'width' | 'dragging' | 'handleProps'>) {
  return (
    <div
      role='separator'
      aria-orientation='vertical'
      aria-label='Resize column'
      aria-valuenow={width ? Math.round(width) : undefined}
      aria-valuemin={MIN_COLUMN_WIDTH}
      aria-valuemax={MAX_COLUMN_WIDTH}
      tabIndex={0}
      title='Drag to resize · double-click to reset · ←/→ to nudge'
      {...handleProps}
      className='group/resize absolute right-0 top-0 z-20 flex h-full w-2.5 translate-x-1/2 cursor-col-resize touch-none select-none items-center justify-center focus-visible:outline-none'
    >
      <span
        aria-hidden
        className={`w-0.5 rounded-full transition-all ${
          dragging
            ? 'h-full bg-primary'
            : 'h-1/2 bg-border group-hover/resize:h-full group-hover/resize:bg-primary group-focus-visible/resize:h-full group-focus-visible/resize:bg-primary'
        }`}
      />
    </div>
  )
}

/**
 * The standard sort indicator: two stacked carets (z8nrz7e4h6).
 *
 * Unsorted, both show at a clearly visible tone; sorted, the caret for the
 * direction is solid and the other recedes. Drawn in `currentColor`, so it
 * matches the header text on every table's header background.
 */
function SortCarets({ dir }: { dir: 'asc' | 'desc' | null }) {
  const up = dir === 'asc' ? 'opacity-100' : dir === 'desc' ? 'opacity-30' : 'opacity-60 group-hover/sort:opacity-90'
  const down = dir === 'desc' ? 'opacity-100' : dir === 'asc' ? 'opacity-30' : 'opacity-60 group-hover/sort:opacity-90'
  return (
    <svg aria-hidden viewBox='0 0 10 14' className='h-3.5 w-2.5 shrink-0' fill='currentColor'>
      <path d='M5 1 9 6H1z' className={`transition-opacity ${up}`} />
      <path d='M5 13 1 8h8z' className={`transition-opacity ${down}`} />
    </svg>
  )
}

/**
 * A sortable column's header content: the header, then the sort carets
 * (z8nrz7e4h6). Clicking cycles ascending → descending → the table's own order.
 *
 * A real button so it is keyboard-operable. The carets are `aria-hidden` and the
 * cell keeps its `aria-label`, so the header's accessible name stays exactly the
 * column's — `getByRole('columnheader', { name, exact: true })` still matches —
 * while `aria-sort` on the cell announces the order.
 */
function sortableContent(columnId: string, columns: UseTableColumnsResult, children: ReactNode): ReactNode {
  if (!columns.isSortable(columnId)) return children
  const dir = columns.sort?.id === columnId ? columns.sort.dir : null
  return (
    <button
      type='button'
      onClick={() => columns.cycleSort(columnId)}
      title={dir === 'asc' ? 'Sorted ascending — click for descending' : dir === 'desc' ? 'Sorted descending — click to clear' : 'Click to sort'}
      // Inherits the header's own casing and tracking: Tailwind's preflight resets
      // `text-transform` on buttons, which turned "NAME" back into "Name".
      className='group/sort inline-flex max-w-full items-center gap-1 text-left [letter-spacing:inherit] [text-transform:inherit] focus-visible:underline focus-visible:outline-none'
    >
      <span className='min-w-0'>{children}</span>
      <SortCarets dir={dir} />
    </button>
  )
}

/** `aria-sort` for a sortable column; nothing for one that is not. */
function ariaSortOf(columnId: string, columns: UseTableColumnsResult): 'ascending' | 'descending' | 'none' | undefined {
  if (!columns.isSortable(columnId)) return undefined
  if (columns.sort?.id !== columnId) return 'none'
  return columns.sort.dir === 'asc' ? 'ascending' : 'descending'
}

/**
 * Shared props. `label` is load-bearing, not decoration — see below.
 */
interface ResizableHeadProps {
  columnId: string
  columns: UseTableColumnsResult
  children?: ReactNode
  className?: string
  /** False for a column whose width must not be dragged (e.g. an actions cell). */
  resizable?: boolean
  /**
   * The column's name, applied as `aria-label` on the cell.
   *
   * **Required whenever the handle renders, and here is why.** A header's
   * accessible name is otherwise computed from its contents, and the resize
   * handle is one of those contents — so `<th>Email<div role="separator"
   * aria-label="Resize column"/></th>` is named "Email Resize column", not
   * "Email". That silently breaks anything matching a header by exact name,
   * including `getByRole('columnheader', { name: 'Email', exact: true })` in
   * e2e/tests/smoke/configure.smoke.spec.ts, and it degrades the announcement
   * for a screen-reader user reading the table.
   *
   * `aria-label` takes precedence over name-from-content, so setting it pins the
   * name to the column's own. It also gives a header with no visible text (an
   * expand chevron, an actions cell) a real name instead of an empty one.
   */
  label?: string
  /**
   * Label for the Columns menu, on a panel with two tables so the two are
   * distinguishable. Only read on the first visible column, where it renders.
   */
  menuLabel?: string
  /**
   * Hover text for the header cell. Some columns explain themselves there
   * rather than in the label — "Prep (h)" on the process Equipment tab says
   * that prep delays the reservation — and that text is content worth keeping
   * when the header becomes configurable.
   */
  title?: string
  /**
   * Whether a right-pinned column currently has content hidden underneath it,
   * from `useStickyColumnEdge`. Only tables declaring a `sticky` column pass
   * it; the header has to be told because it cannot see the body's scroll.
   */
  stickyEdge?: boolean
}

/** The pinning declared on this column, if any. */
function stickyOf(columnId: string, columns: ResizableHeadProps['columns']): boolean {
  return columns.all.find(c => c.def.id === columnId)?.def.sticky === 'right'
}

/**
 * A resizable, per-user-width `TableHead` for tables built on the shadcn
 * `Table` primitives. See `ResizableTh` for the raw-`<table>` equivalent and
 * `useColumnResize` for the behaviour both share.
 */
export function ResizableTableHead({
  columnId,
  columns,
  children,
  className,
  resizable = true,
  label,
  menuLabel,
  title,
  stickyEdge = false,
}: ResizableHeadProps) {
  const { cellRef, width, pinned, dragging, handleProps } = useColumnResize(columnId, columns)
  const menu = rendersColumnsMenu(columnId, columns)
  const sticky = stickyOf(columnId, columns)
  return (
    <TableHead
      ref={cellRef}
      aria-label={label ?? (menu || columns.isSortable(columnId) ? headerNameOf(columnId, columns) : undefined)}
      aria-sort={ariaSortOf(columnId, columns)}
      title={title}
      className={`${resizable || menu ? 'relative' : ''} ${menu ? 'pl-10' : ''} ${width && pinned ? 'overflow-hidden' : ''} ${sticky ? stickyCellClass('head', stickyEdge) : ''} ${className ?? ''}`}
      style={width ? (pinned ? { width, minWidth: width, maxWidth: width } : { width }) : undefined}
    >
      {sortableContent(columnId, columns, children)}
      {menu && (
        <span className='absolute left-1 top-1/2 -translate-y-1/2'>
          <ColumnsMenu columns={columns} label={menuLabel} />
        </span>
      )}
      {resizable && (
        <ColumnResizeHandle width={width} dragging={dragging} handleProps={handleProps} />
      )}
    </TableHead>
  )
}

/**
 * The same thing for a hand-rolled `<table>`: renders a plain `<th>`, so a page
 * with its own header styling keeps it and gains the resize grip.
 *
 * `className` is passed through untouched — these tables carry their own padding
 * and alignment classes, and a wrapper that normalized them would change how
 * every one of those headers looks.
 */
export function ResizableTh({
  columnId,
  columns,
  children,
  className,
  resizable = true,
  label,
  menuLabel,
  title,
  stickyEdge = false,
}: ResizableHeadProps) {
  const { cellRef, width, pinned, dragging, handleProps } = useColumnResize(columnId, columns)
  const menu = rendersColumnsMenu(columnId, columns)
  const sticky = stickyOf(columnId, columns)
  return (
    <th
      ref={cellRef}
      aria-label={label ?? (menu || columns.isSortable(columnId) ? headerNameOf(columnId, columns) : undefined)}
      aria-sort={ariaSortOf(columnId, columns)}
      title={title}
      className={`${resizable || menu ? 'relative' : ''} ${menu ? 'pl-10' : ''} ${width && pinned ? 'overflow-hidden' : ''} ${sticky ? stickyCellClass('head', stickyEdge) : ''} ${className ?? ''}`}
      // A stored width must beat the utility width class these tables declare
      // (`w-40`, `w-[22rem]`), so it is applied inline where specificity wins.
      // Only a DRAGGED width pins; a default stays a preference the layout may
      // compress — see ColumnCells.sized.
      style={width ? (pinned ? { width, minWidth: width, maxWidth: width } : { width }) : undefined}
    >
      {sortableContent(columnId, columns, children)}
      {menu && (
        <span className='absolute left-1 top-1/2 -translate-y-1/2'>
          <ColumnsMenu columns={columns} label={menuLabel} />
        </span>
      )}
      {resizable && (
        <ColumnResizeHandle width={width} dragging={dragging} handleProps={handleProps} />
      )}
    </th>
  )
}

/**
 * The column's declared header, used as the cell's accessible name when the
 * caller gave none and the menu is about to render inside it.
 *
 * Without this the button's own `aria-label` would be folded into the cell's
 * name-from-content — "Actions Columns" instead of "Actions" — which breaks
 * every `getByRole('columnheader', { name, exact: true })` in the e2e suite,
 * exactly as the resize handle does. See `label` above.
 */
function headerNameOf(columnId: string, columns: UseTableColumnsResult): string | undefined {
  return columns.all.find((c) => c.def.id === columnId)?.def.header
}
